import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Warning as WarningIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Report = () => {
  const navigate = useNavigate();
  const [verificationItems] = useState([
    {
      id: '101',
      ruleType: 'Address Verification',
      description: 'The address on the application does not match the address shown on MVR or Auto+ reports.',
      remedy: 'Please provide proof of address (utility bill, lease agreement, etc.) that matches the application address.',
      remarks: '',
    },
    {
      id: '102',
      ruleType: 'Winter Tire Discount',
      description: 'Winter tire discount has been applied to the policy.',
      remedy: 'Please provide photos of winter tires showing the winter tire symbol and proof of installation.',
      remarks: '',
    },
    {
      id: '103',
      ruleType: 'Liability Coverage',
      description: 'Policy has $1,000,000 liability coverage selected.',
      remedy: 'Confirmation required that $2,000,000 liability coverage was offered and declined.',
      remarks: 'Please add application remark note: Client declined $2,000,000 liability coverage option.',
    },
    {
      id: '104',
      ruleType: 'OPCF 20/27 Coverage',
      description: 'Policy does not include OPCF 20/27 coverage.',
      remedy: 'Confirmation required that OPCF 20/27 coverage was offered and declined.',
      remarks: 'Please add application remark note: Client declined OPCF 20/27 coverage option.',
    },
    {
      id: '105',
      ruleType: 'OPCF 43 Coverage',
      description: 'New vehicle without OPCF 43 coverage.',
      remedy: 'Confirmation required that OPCF 43 coverage was offered and declined for the new vehicle.',
      remarks: 'Please add application remark note: Client declined OPCF 43 coverage for new vehicle.',
    },
    {
      id: '106',
      ruleType: 'Commute Usage',
      description: 'Vehicle shows 0 km commute distance.',
      remedy: 'Please provide explanation for no commute usage.',
      remarks: 'Please add application remark note: Explanation required for 0 km commute distance.',
    },
    {
      id: '107',
      ruleType: 'Low Annual Mileage',
      description: 'Annual mileage is reported below 10,000 km.',
      remedy: 'Please provide explanation for low annual mileage.',
      remarks: 'Please add application remark note: Explanation required for annual mileage below 10,000 km.',
    },
    {
      id: '108',
      ruleType: 'Accident History',
      description: 'Discrepancy found between Auto+ report accidents and quoted accidents.',
      remedy: 'Please review and explain any differences between Auto+ accident history and quoted accidents.',
      remarks: 'Please add application remark note: Explanation required for accident history discrepancy.',
    },
    {
      id: '109',
      ruleType: 'Conviction History',
      description: 'Discrepancy found between MVR convictions and quoted convictions.',
      remedy: 'Please review and explain any differences between MVR conviction history and quoted convictions.',
      remarks: 'Please add application remark note: Explanation required for conviction history discrepancy.',
    },
    {
      id: '001',
      ruleType: 'Winter Tire Discount',
      description: 'The Quote indicates Winter Tire Discount is applied. Documentation of winter tire purchase or installation is required.',
      remedy: 'Please provide a valid winter tire invoice or installation receipt showing the vehicle information or license plate.',
      remarks: '',
    },
    {
      id: '006',
      ruleType: 'Market Approval',
      description: 'The Quote indicates market approval is required. Market approval must be provided for any quote that contains a "Refer to market" message.',
      remedy: 'Provide the name of the underwriter and confirmation code when prompted on the submission form.',
      remarks: '',
    },
    {
      id: '008',
      ruleType: 'Mismatched Address',
      description: 'The address on the application does not match the address on one or more of the MVRs or AutoPlus provided.',
      remedy: 'Documentation containing the address on the application and the name of the driver in question must be attached to the submission before proceeding to the binding stage.',
      remarks: '',
    },
    {
      id: '012',
      ruleType: 'Pleasure Use',
      description: 'The application indicates that one or more vehicles on the policy are not used for commute to work or school.',
      remarks: '',
    },
    {
      id: '013',
      ruleType: 'Business Use',
      description: 'The application indicates that one or more of the vehicles have business use applied.',
      remarks: '',
    },
    {
      id: '014',
      ruleType: 'Low Annual Driving Distance',
      description: 'The application indicates that one or more vehicles on the policy has an annual driving distance less than 15,000km. These kilometers may be underreported.',
      remarks: '',
    },
    {
      id: '021',
      ruleType: 'Possible Unlisted Vehicles',
      description: 'Vehicles found on a prior policy for one or more driver are not included on the application. Review the AutoPlus for each driver and verify the reason why vehicles that appear on the AutoPlus in the last 12 months are not included in the policy. All vehicles in the household must be listed on the application, or insurance information must be provided.',
      remedy: 'Detailed remarks explaining why these vehicles are not included on the application or insurance information for the vehicle(s) is required if this vehicle is in the household.',
      remarks: '',
    },
  ].sort((a, b) => {
    const aNum = parseInt(a.id);
    const bNum = parseInt(b.id);
    if (aNum >= 100 && bNum >= 100) return aNum - bNum;
    if (aNum >= 100) return -1;
    if (bNum >= 100) return 1;
    return aNum - bNum;
  }));

  const handleHandover = () => {
    navigate('/handover');
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/verify')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Verification
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Verification Report
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review and finalize the document verification results
        </Typography>

        {/* Next Button */}
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={() => navigate('/handover')}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Submit Report
        </Button>
      </Box>

      {/* Table Content */}
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>UW ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '180px' }}>Validation Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Result</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rule Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Remedy</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Product</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {verificationItems.map((item) => (
              <TableRow 
                key={item.id}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.ruleType}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: '#ff9800' }} />
                    <Typography>Caution</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: '400px' }}>
                  <Typography variant="body2">
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: '300px' }}>
                  <Typography variant="body2">
                    {item.remedy}
                  </Typography>
                  {item.remarks && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please add application remark note: {item.remarks}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label="Auto" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#1976d2',
                      color: 'white',
                    }} 
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Report;
